import React, { useCallback } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import GenderIcon from '../common/GenderIcon';
import OptimizedImage from './OptimizedImage';

const {
  FiX, FiMoreVertical, FiCalendar, FiClock,
  FiMapPin, FiShield, FiEye, FiShare2, FiEdit, FiTrash2
} = FiIcons;

// ✅ 將 action 按鈕提取為獨立的 memoized 組件，避免 ListCard re-render 時重建 inline 函數
const ActionButtons = React.memo(({ pet, onPreview, onShare, onEdit, onDelete, t }) => {
  const handlePreview = useCallback(() => onPreview(pet), [onPreview, pet]);
  const handleShare   = useCallback(() => onShare(pet),   [onShare, pet]);
  const handleEdit    = useCallback(() => onEdit(pet.id), [onEdit, pet.id]);
  const handleDelete  = useCallback(() => onDelete(pet),  [onDelete, pet]);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      <button onClick={handlePreview} className="flex items-center justify-center gap-2 px-3 py-2 text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors text-sm font-medium">
        <SafeIcon icon={FiEye} className="h-4 w-4" />
        <span className="hidden sm:inline">{t('dashboard.preview')}</span>
      </button>
      <button onClick={handleShare} className="flex items-center justify-center gap-2 px-3 py-2 text-green-600 bg-green-50 hover:bg-green-100 rounded-lg transition-colors text-sm font-medium">
        <SafeIcon icon={FiShare2} className="h-4 w-4" />
        <span className="hidden sm:inline">{t('dashboard.share')}</span>
      </button>
      <button onClick={handleEdit} className="flex items-center justify-center gap-2 px-3 py-2 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors text-sm font-medium">
        <SafeIcon icon={FiEdit} className="h-4 w-4" />
        <span className="hidden sm:inline">{t('dashboard.edit')}</span>
      </button>
      <button onClick={handleDelete} className="flex items-center justify-center gap-2 px-3 py-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors text-sm font-medium">
        <SafeIcon icon={FiTrash2} className="h-4 w-4" />
        <span className="hidden sm:inline">{t('dashboard.delete')}</span>
      </button>
    </div>
  );
});

const ListCard = React.memo(({ pet, expandedPetId, setExpandedPetId, onPreview, onShare, onEdit, onDelete, t, translatePetType }) => {
  const isExpanded = expandedPetId === pet.id;

  // ✅ 穩定 toggle 函數引用，避免每次 render 建立新 arrow function
  const handleToggle = useCallback(() => {
    setExpandedPetId(isExpanded ? null : pet.id);
  }, [isExpanded, pet.id, setExpandedPetId]);

  return (
    <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-200 border border-gray-200 overflow-hidden">
      <div className="p-4 sm:p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 relative">
            <OptimizedImage
              src={pet.photo_url}
              alt={pet.name}
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-4 border-gray-200"
              fallbackContent={
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-xl sm:text-2xl font-bold">
                  {pet.name ? pet.name.charAt(0).toUpperCase() : '?'}
                </div>
              }
            />
            <div className="absolute -bottom-1 -right-1">
              <GenderIcon gender={pet.gender} petType={pet.type} size="sm" showBackground={true} className="shadow-lg border-2 border-white" />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 truncate">
                  {pet.name || t('dashboard.unnamedPet')}
                </h3>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <span className="px-2 py-0.5 bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 rounded-full text-xs font-medium capitalize">
                    {translatePetType(pet.type)}
                  </span>
                  {pet.breed && <span className="text-sm text-gray-600 truncate">{pet.breed}</span>}
                </div>
              </div>
              {/* ✅ 使用穩定的 handleToggle 而非 inline arrow */}
              <button onClick={handleToggle} className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0">
                <SafeIcon icon={isExpanded ? FiX : FiMoreVertical} className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs sm:text-sm text-gray-600 mt-3">
              <div className="flex items-center gap-1.5 min-w-0">
                <SafeIcon icon={FiCalendar} className="text-blue-600 w-4 h-4 flex-shrink-0" />
                <span className="font-medium flex-shrink-0">{t('dashboard.nextFeeding')}:</span>
                <span className="truncate">{pet._computed?.nextFeeding || t('dashboard.notSet')}</span>
              </div>
              <div className="flex items-center gap-1.5 min-w-0">
                <SafeIcon icon={FiClock} className="text-purple-600 w-4 h-4 flex-shrink-0" />
                <span className="font-medium flex-shrink-0">{t('dashboard.nextMedication')}:</span>
                <span className="truncate">{pet._computed?.nextMedication || t('dashboard.notSet')}</span>
              </div>
            </div>
          </div>
        </div>

        {isExpanded && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs sm:text-sm text-gray-600 mb-4">
              <div className="flex items-center gap-1.5">
                <SafeIcon icon={FiMapPin} className="text-orange-600 w-4 h-4 flex-shrink-0" />
                <span className="font-medium">{t('dashboard.location')}:</span>
                <span className="truncate">{pet.current_location || t('dashboard.home')}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <SafeIcon icon={FiShield} className="text-green-600 w-4 h-4 flex-shrink-0" />
                <span className="font-medium">{t('dashboard.nextVetVisit')}:</span>
                <span className="truncate">{pet.next_vaccine_date || t('dashboard.notSet')}</span>
              </div>
            </div>
            {/* ✅ ActionButtons 是獨立 memo 組件，自帶穩定 handler */}
            <ActionButtons
              pet={pet}
              onPreview={onPreview}
              onShare={onShare}
              onEdit={onEdit}
              onDelete={onDelete}
              t={t}
            />
          </div>
        )}
      </div>
    </div>
  );
});

export default ListCard;